import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStaff } from '../../contexts/StaffContext';
import { useVacation } from '../../contexts/VacationContext';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase-config';
import { collection, getDocs } from 'firebase/firestore';
import StaffForm from '../../components/staff/StaffForm';
import '../../styles/staff-forms-modals.css';

const StaffFormPage = ({ isEdit = false }) => {
  const { t } = useTranslation('staff');
  const { t: tCommon } = useTranslation('common');
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  const { fetchStaffMember, createStaffMember, updateStaffMember } = useStaff();
  const { createVacationAccount, initializeAccountWithCarryover } = useVacation();
  
  const [staff, setStaff] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [error, setError] = useState(null);
  const [dataInitialized, setDataInitialized] = useState(false);
  
  // Fetch departments directly from Firestore - do this first before any other operations
  useEffect(() => {
    const fetchDepartmentsDirectly = async () => {
      if (!companyId) {
        console.log("No company ID available yet");
        return;
      }
      
      setLoadingDepartments(true);
      try {
        console.log("Directly fetching departments for staff form for company:", companyId);
        const departmentsRef = collection(db, 'companies', companyId, 'departments');
        const snapshot = await getDocs(departmentsRef);
        
        const departmentsList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || 'Unknown Department',
          ...doc.data()
        }));
        
        console.log(`Successfully loaded ${departmentsList.length} departments for staff form`);
        setDepartments(departmentsList);
        setError(null);
      } catch (err) {
        console.error('Error loading departments:', err);
        setError('Failed to load departments: ' + err.message);
      } finally {
        setLoadingDepartments(false);
        setDataInitialized(true);
      }
    };
    
    fetchDepartmentsDirectly();
  }, [companyId]);
  
  // Fetch staff data if in edit mode - but only after departments are loaded
  useEffect(() => {
    const fetchData = async () => {
      if (isEdit && staffId && !loadingDepartments && departments.length > 0) {
        try {
          setLoading(true);
          console.log("Fetching staff member with ID:", staffId);
          const staffData = await fetchStaffMember(staffId);
          setStaff(staffData);
          setError(null);
        } catch (err) {
          console.error('Error fetching staff data:', err);
          setError('Failed to load staff member data: ' + err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchData();
  }, [isEdit, staffId, fetchStaffMember, loadingDepartments, departments.length]);
  
  // Handle form submission
  const handleSubmit = async (submissionData) => {
    try {
      setLoading(true);
      
      if (isEdit) {
        // Edit mode - only update staff, ignore vacation data
        await updateStaffMember(staffId, submissionData.staff);
        navigate(`/admin/staff/${staffId}`);
      } else {
        // Create mode - create staff first, then vacation account
        const newStaff = await createStaffMember(submissionData.staff);
        
        // Create vacation account if data provided
        if (submissionData.vacationData && newStaff.id) {
          try {
            const currentYear = new Date().getFullYear().toString();
            const vacData = submissionData.vacationData;
            
            console.log('Creating vacation account for new staff member:', {
              staffId: newStaff.id,
              year: currentYear,
              vacationData: vacData
            });
            
            if (vacData.autoCalculateCarryover) {
              // Use automatic carryover calculation
              await initializeAccountWithCarryover(
                newStaff.id,
                currentYear,
                parseFloat(vacData.baseEntitlement),
                parseFloat(vacData.carryOverLimit)
              );
              console.log('✅ Vacation account created with automatic carryover calculation');
            } else {
              // Manual vacation account creation
              await createVacationAccount(newStaff.id, currentYear, {
                baseEntitlement: parseFloat(vacData.baseEntitlement),
                carryOver: 0,
                carryOverLimit: parseFloat(vacData.carryOverLimit),
                carryOverDeadline: new Date(vacData.carryOverDeadline)
              });
              console.log('✅ Vacation account created with manual settings');
            }
          } catch (vacError) {
            console.error('⚠️ Failed to create vacation account:', vacError);
            // Don't fail the whole operation, just log the error
            // Staff member was created successfully, vacation can be added later
            setError('Staff member created, but failed to create vacation account. You can add it manually from the staff detail page.');
          }
        }
        
        navigate(`/admin/staff/${newStaff.id}`);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error saving staff data:', err);
      setError('Failed to save staff member: ' + err.message);
      setLoading(false);
    }
  };
  
  // Handle cancel/back action
  const handleCancel = () => {
    if (isEdit && staffId) {
      navigate(`/admin/staff/${staffId}`);
    } else {
      navigate('/admin/staff');
    }
  };
  
  // If we're still in the initial loading phase
  if (!dataInitialized || (loadingDepartments && !departments.length)) {
    return (
      <div className="staff-form-page">
        <div className="staff-form-header">
          <h1>{isEdit ? t('page.editTitle') : t('page.createTitle')}</h1>
          <button onClick={handleCancel} className="cancel-button">
            {t('buttons.cancel')}
          </button>
        </div>
        <div className="loading-departments">{t('page.loadingDepartments')}</div>
      </div>
    );
  }
  
  // If we're in edit mode and still loading the staff data
  if (isEdit && !staff && loading) {
    return (
      <div className="staff-form-page">
        <div className="staff-form-header">
          <h1>{t('page.editTitle')}</h1>
          <button onClick={handleCancel} className="cancel-button">
            {t('buttons.cancel')}
          </button>
        </div>
        <div className="loading-staff">{t('page.loadingStaffData')}</div>
      </div>
    );
  }
  
  // If departments loaded but there are none (rare case)
  if (departments.length === 0 && !loadingDepartments) {
    return (
      <div className="staff-form-page">
        <div className="staff-form-header">
          <h1>{isEdit ? t('page.editTitle') : t('page.createTitle')}</h1>
          <button onClick={handleCancel} className="cancel-button">
            {t('buttons.cancel')}
          </button>
        </div>
        <div className="no-departments">
          <p>{t('page.noDepartments')}</p>
          <button onClick={() => navigate('/admin/departments')}>
            {t('page.goToDepartments')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="staff-form-page">
      <div className="staff-form-header">
        <h1>{isEdit ? t('page.editTitle') : t('page.createTitle')}</h1>
        <button onClick={handleCancel} className="cancel-button">
          {t('buttons.cancel')}
        </button>
      </div>
      
      <StaffForm 
        staff={isEdit ? staff : null}
        departments={departments}
        onSubmit={handleSubmit}
        isLoading={loading}
        error={error}
      />
    </div>
  );
};

export default StaffFormPage;